/**
 * CSV Import Results Component
 *
 * Displays import completion summary with success/failure counts
 * and an expandable table of failed rows with error details.
 */

import { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { ImportJobProgress, ImportJobFailure, ImportJobStatus } from '@/types/user-migration';

interface CsvImportResultsProps {
  /** Job progress data with final counts */
  progress: ImportJobProgress;
  /** Final job status */
  status: ImportJobStatus;
  /** List of failed rows (max 100) */
  failures?: ImportJobFailure[];
  /** Whether the import was cancelled */
  wasCancelled?: boolean;
}

/**
 * Format number with commas
 */
function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Summary Card Component
 */
function SummaryCard({
  label,
  value,
  variant = 'default',
  icon: Icon,
}: {
  label: string;
  value: number;
  variant?: 'default' | 'success' | 'warning' | 'error';
  icon: React.ComponentType<{ className?: string }>;
}) {
  const variantStyles = {
    default: 'bg-muted/50 text-foreground',
    success: 'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400',
    warning: 'bg-yellow-50 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-400',
    error: 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400',
  };

  return (
    <div className={cn('rounded-lg px-4 py-3 flex items-center gap-3', variantStyles[variant])}>
      <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
      <div>
        <div className="text-2xl font-semibold">{formatNumber(value)}</div>
        <div className="text-sm opacity-80">{label}</div>
      </div>
    </div>
  );
}

/**
 * Copy Button Component
 */
function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard is not available
    }
  };

  return (
    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={handleCopy} aria-label={label}>
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </Button>
  );
}

/**
 * CSV Import Results Component
 */
export function CsvImportResults({
  progress,
  status,
  failures = [],
  wasCancelled = false,
}: CsvImportResultsProps) {
  const [showFailures, setShowFailures] = useState(failures.length > 0 && failures.length <= 10);

  const hasFailures = failures.length > 0;
  // Make conditions mutually exclusive - wasCancelled takes priority
  const isCancelled = status === 'cancelled' || wasCancelled;
  const isFailed = status === 'failed' && !isCancelled;
  const isSuccess = status === 'completed' && progress.failedRows === 0 && !isCancelled;
  const isPartialSuccess = status === 'completed' && progress.failedRows > 0 && !isCancelled;

  // Calculate success rate
  const successRate =
    progress.totalRows > 0
      ? Math.round(((progress.createdRows + progress.skippedRows) / progress.totalRows) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Status Header */}
      <div
        className={cn(
          'flex items-center gap-3 p-4 rounded-lg',
          isSuccess && 'bg-green-50 dark:bg-green-950/20',
          isPartialSuccess && 'bg-yellow-50 dark:bg-yellow-950/20',
          isFailed && 'bg-red-50 dark:bg-red-950/20',
          isCancelled && 'bg-yellow-50 dark:bg-yellow-950/20'
        )}
      >
        {isSuccess && (
          <>
            <CheckCircle2 className="h-6 w-6 text-green-600" aria-hidden="true" />
            <div>
              <div className="font-semibold text-green-700 dark:text-green-400">
                Import Completed Successfully
              </div>
              <div className="text-sm text-green-600 dark:text-green-500">
                All {formatNumber(progress.totalRows)} rows processed without errors
              </div>
            </div>
          </>
        )}

        {isPartialSuccess && (
          <>
            <AlertCircle className="h-6 w-6 text-yellow-600" aria-hidden="true" />
            <div>
              <div className="font-semibold text-yellow-700 dark:text-yellow-400">
                Import Completed with Errors
              </div>
              <div className="text-sm text-yellow-600 dark:text-yellow-500">
                {formatNumber(progress.failedRows)} of {formatNumber(progress.totalRows)} rows
                failed ({successRate}% success rate)
              </div>
            </div>
          </>
        )}

        {isFailed && (
          <>
            <XCircle className="h-6 w-6 text-red-600" aria-hidden="true" />
            <div>
              <div className="font-semibold text-red-700 dark:text-red-400">Import Failed</div>
              <div className="text-sm text-red-600 dark:text-red-500">
                The import process encountered a critical error
              </div>
            </div>
          </>
        )}

        {isCancelled && (
          <>
            <AlertCircle className="h-6 w-6 text-yellow-600" aria-hidden="true" />
            <div>
              <div className="font-semibold text-yellow-700 dark:text-yellow-400">
                Import Cancelled
              </div>
              <div className="text-sm text-yellow-600 dark:text-yellow-500">
                {formatNumber(progress.processedRows)} of {formatNumber(progress.totalRows)} rows
                were processed before cancellation
              </div>
            </div>
          </>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard
          label="Total Rows"
          value={progress.totalRows}
          variant="default"
          icon={({ className }) => (
            <div className={cn('w-5 h-5 rounded-full bg-current opacity-20', className)} />
          )}
        />
        <SummaryCard
          label="Created"
          value={progress.createdRows}
          variant="success"
          icon={CheckCircle2}
        />
        <SummaryCard
          label="Skipped"
          value={progress.skippedRows}
          variant="warning"
          icon={AlertCircle}
        />
        <SummaryCard label="Failed" value={progress.failedRows} variant="error" icon={XCircle} />
      </div>

      {/* Failures Table */}
      {hasFailures && (
        <div className="space-y-3">
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
            onClick={() => setShowFailures(!showFailures)}
            aria-expanded={showFailures}
            aria-controls="failures-table"
          >
            {showFailures ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {showFailures ? 'Hide' : 'Show'} Error Details ({formatNumber(failures.length)}
            {progress.failedRows > 100 && ` of ${formatNumber(progress.failedRows)}`})
          </button>

          {showFailures && (
            <div id="failures-table" className="border rounded-lg overflow-hidden">
              <ScrollArea className="max-h-[300px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                    <TableRow>
                      <TableHead className="w-[80px]">Row #</TableHead>
                      <TableHead className="w-[200px]">Email</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {failures.map(failure => (
                      <TableRow key={`${failure.row}-${failure.email}`}>
                        <TableCell className="font-mono text-sm">{failure.row}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className="truncate max-w-[160px]" title={failure.email}>
                              {failure.email}
                            </span>
                            <CopyButton
                              text={failure.email}
                              label={`Copy email ${failure.email}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {failure.reason}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>

              {progress.failedRows > 100 && (
                <div className="px-4 py-2 bg-muted/50 text-sm text-muted-foreground border-t">
                  Showing first 100 errors. {formatNumber(progress.failedRows - 100)} more errors
                  not displayed.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
